<template>
  <div class="col-12">
    <h1 tabindex="-1" ref="focusFirst" class="sr-only">{{ $t('eGiftCardBuild') }}</h1>
    <div v-if="loading" class="wc-product-row__spinner-wrapper d-flex">
      <WCSpinner class="mx-auto align-self-center" size="xl" />
    </div>
    <div class="row" v-if="!loading">
      <div class="col-12" v-if="getLineItems.length > 0">
        <div class="alert alert-danger" role="alert">
          {{ $t('eGiftCardPurchaseAlert') }}
        </div>
      </div>
      <div class="col-12">
        <div
          class="d-flex flex-column flex-md-row justify-content-between align-items-baseline border-secondary border-bottom mb-2"
        >
          <h1>{{ $t('eGiftCardBuild') }}</h1>
          <span v-if="allowCheck">
            {{ $t('eGiftCardCheck') }}
            <router-link
              class="btn-link underline-link-from-center ml-1 font-weight-bold"
              :to="`/gc/check`"
              >{{ $t('checkYourBalance') }}</router-link
            >
          </span>
        </div>
      </div>

      <div class="col-12 col-md-8">
        <WCGiftCardBuilderForm @update:value="formChanged" @update:valid="validationChanged" />
      </div>
      <div class="col-12 col-md-4 text-center">
        <p class="d-block d-sm-none text-left border-bottom border-secondary font-size-lg">
          {{ $t('preview') }}
        </p>
        <WCGiftCardPreview class="text-left" :giftCard="giftCard" />
        <button
          class="btn btn-primary btn-block mt-2"
          id="gift-card-add-to-cart__button"
          :disabled="!gcProfile || getLineItems.length > 0 || !giftCard.isValid || !giftCard.amount"
          @click="addGiftCard()"
        >
          {{ $t('addToCart') }}
        </button>
        <button
          id="gift-card-policy__button"
          class="btn btn-link underline-link-from-center"
          @click="open"
          ref="giftCardPolicyBtnRef"
        >
          {{ $t('eGiftCardPolicy') }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import { mapActions, mapGetters } from 'vuex';
import ModalService from '@/modules/modals/services/modal.service';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import FocusMixin from '@/mixins/FocusMixin';
import WCGiftCardBuilderForm from '../components/WCGiftCardBuilderForm/WCGiftCardBuilderForm.vue';
import WCGiftCardPreview from '../components/WCGiftCardPreview/WCGiftCardPreview.vue';
import WCGiftCardPolicyModal from '../components/WCGiftCardPolicyModal/WCGiftCardPolicyModal.vue';
import WCGiftCardCartModal from '../components/WCGiftCardCartModal/WCGiftCardCartModal.vue';

export default {
  name: 'GiftCardBuilder',
  components: {
    WCGiftCardBuilderForm,
    WCGiftCardPreview,
    WCSpinner,
  },
  data() {
    return {
      loading: false,
      gcProfile: {},
      isAllowed: Boolean,
      allowCheck: Boolean,
      giftCard: {
        id: '',
        amount: '',
        design: null,
        quantity: 1,
        recipientEmail: null,
        recipientName: '',
        recipientPhoneNumber: null,
        senderName: '',
        message: '',
        print: false,
        sendDate: '',
        deliveryMethod: [],
        isValid: false,
      },
    };
  },
  mixins: [FocusMixin], // Mixin for main content focus via routing
  computed: {
    ...mapGetters({
      getLineItems: 'cart/getLineItems',
    }),
  },

  async mounted() {
    try {
      this.loading = true;
      this.gcProfile = this.$configuration.giftCardProfile;
      this.isAllowed = this.$configuration.allowEGiftCards;
      this.allowCheck = this.$configuration.allowGiftCardCheck;
      this.loading = false;
      await this.checkCart();
    } catch (error) {
      ToastService.open(WCSimpleToast, {
        props: {
          variant: 'danger',
          title: 'Error',
          message: error.message,
        },
        timeout: 7000,
      });
    }
  },
  methods: {
    formChanged(form) {
      this.giftCard.recipientName = form.recipientName;
      this.giftCard.senderName = form.senderName;
      this.giftCard.message = form.message;
      this.giftCard.recipientPhoneNumber = form.recipientPhoneNumber;
      this.giftCard.recipientEmail = form.recipientEmail;
      this.giftCard.deliveryMethod = form.deliveryMethod;
      this.giftCard.design = form.design;
      this.giftCard.amount = form.amount;
      this.giftCard.sendDate = null;
      if (form.selected === 'sendLater') {
        this.giftCard.sendDate = form.sendDate;
      }
      if (form.deliveryMethod.includes('Print')) {
        this.giftCard.print = true;
      }
    },

    ...mapActions({
      add: 'cart/addGiftCard',
      remove: 'cart/removeGiftCard',
    }),
    async addGiftCard() {
      try {
        if (!this.giftCard.deliveryMethod.includes('Email')) {
          this.giftCard.recipientEmail = null;
        }
        if (!this.giftCard.deliveryMethod.includes('Text')) {
          this.giftCard.recipientPhoneNumber = null;
        }

        if (!this.giftCard.isValid || this.giftCard.amount < 0) {
          // eslint-disable-next-line no-throw-literal
          throw 'Invalid Gift Card';
        }

        await this.add(this.giftCard);

        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'success',
            title: this.$t('eGiftCardCartAddSuccess'),
            message: this.$t('eGiftCardCartAddSuccessMessage'),
          },
          timeout: 5000,
        });
      } catch (error) {
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: 'Error',
            message: error.message ? error.message : error,
          },
          timeout: 5000,
        });
      } finally {
        if (this.$configuration.orderTypesEnabled) {
          await this.getCarts();
        }
      }
    },
    async removeGiftCard(id) {
      try {
        await this.remove(id);
      } catch (error) {
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: 'Error',
            message: error.message,
          },
          timeout: 5000,
        });
      }
    },
    validationChanged(valid) {
      this.giftCard.isValid = valid;
    },
    async open() {
      try {
        await ModalService.open(WCGiftCardPolicyModal, { policy: this.gcProfile.policy });
      } catch (error) {
        // NOOP
      } finally {
        if (this.$refs.giftCardPolicyBtnRef) this.$refs.giftCardPolicyBtnRef.focus();
      }
    },
    async checkCart() {
      try {
        if (this.getLineItems.length > 0) {
          await ModalService.open(WCGiftCardCartModal, {});
        }
      } catch (error) {
        // NOOP
      } finally {
        this.$refs.focusFirst.focus();
      }
    },
  },
};
</script>
